if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.railVerticalMql = window.matchMedia('(min-width: 990px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        this.bindRailNav();

        // CSS can't reliably match the rail height to the main image, so measure it. Sync before first paint to pick the right arrows.
        this.syncThumbnailHeight = this.syncThumbnailHeight.bind(this);
        this.syncThumbnailHeight();
        new ResizeObserver(this.syncThumbnailHeight).observe(this.elements.viewer);
        this.railVerticalMql.addEventListener('change', this.syncThumbnailHeight);

        window.addEventListener('resize', debounce(this.updateRailNavVisibility.bind(this), 200));
      }

      syncThumbnailHeight() {
        if (!this.elements.thumbnails) return;
        if (!this.railVerticalMql.matches) {
          this.elements.thumbnails.style.height = '';
        } else {
          this.elements.thumbnails.style.height = `${this.elements.viewer.offsetHeight}px`;
        }
        this.updateRailNavVisibility();
      }

      updateRailNavVisibility() {
        const list = this.elements.thumbnails.querySelector('.thumbnail-list');
        const upButton = this.elements.thumbnails.querySelector('.thumbnail-rail-nav--up');
        const downButton = this.elements.thumbnails.querySelector('.thumbnail-rail-nav--down');
        const prevButton = this.elements.thumbnails.querySelector('.thumbnail-rail-nav--prev');
        const nextButton = this.elements.thumbnails.querySelector('.thumbnail-rail-nav--next');
        if (!list) return;

        const hasOverflow = this.railVerticalMql.matches
          ? list.scrollHeight > list.clientHeight + 1
          : list.scrollWidth > list.clientWidth + 1;

        [upButton, downButton, prevButton, nextButton].forEach((button) => {
          if (button) button.hidden = !hasOverflow;
        });
      }

      bindRailNav() {
        this.elements.thumbnails.addEventListener('click', (event) => {
          const navButton = event.target.closest('.thumbnail-rail-nav');
          if (!navButton) return;
          event.preventDefault();

          const list = this.elements.thumbnails.querySelector('.thumbnail-list');
          const item = list?.querySelector('.thumbnail-list__item');
          if (!list || !item) return;

          if (this.railVerticalMql.matches) {
            const isUp = navButton.classList.contains('thumbnail-rail-nav--up');
            const isDown = navButton.classList.contains('thumbnail-rail-nav--down');
            if (!isUp && !isDown) return;
            const gap = parseFloat(getComputedStyle(list).rowGap) || 0;
            list.scrollTop += (isUp ? -1 : 1) * (item.offsetHeight + gap);
          } else {
            const isPrev = navButton.classList.contains('thumbnail-rail-nav--prev');
            const isNext = navButton.classList.contains('thumbnail-rail-nav--next');
            if (!isPrev && !isNext) return;
            const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
            list.scrollLeft += (isPrev ? -1 : 1) * (item.offsetWidth + gap);
          }
        });
      }

      onSlideChanged(event) {
        if (!event.detail.currentElement) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this.syncThumbnailHeight();

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.elements.thumbnails.slider.scrollTo({ top: thumbnail.offsetTop });
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (!deferredMedia) return;
        requestAnimationFrame(() => {
          deferredMedia.loadContent(false);
          const video = deferredMedia.querySelector('video');
          if (video) {
            video.currentTime = 0;
            video.play();
            if (!activeItem.dataset.hoverControlsBound) {
              activeItem.dataset.hoverControlsBound = 'true';
              activeItem.addEventListener('mouseenter', () => (video.controls = true));
              activeItem.addEventListener('mouseleave', () => (video.controls = false));
            }
          }
        });
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
